import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import FormInputDropDown from 'components/blocks/ManagementNewForms/Inputs/FormInputDropDown';
import FormInputText from 'components/blocks/ManagementNewForms/Inputs/FormInputTexto';

export type PrestacionFormOption = {
    prestacionId: string;
    expedienteId: string | number | null;
    codigoExpediente: string;
    titulo: string;
};

type FormPrestacionesProps = {
    info: boolean;
    baseName?: string;
    origen?: string | number | null;
    expedienteId?: string | number | null;
    label?: string;
    required?: string;
    xl?: number;
    md?: number;
    col?: number;
    onErrorChange?: (error: string | null) => void;
};

const toComparableString = (value: any) => {
    if (value === null || value === undefined) return '';
    return String(value);
};

const getFirstValue = (...values: any[]) => values.find(v => v !== undefined && v !== null && v !== '');

export const buildPrestacionesSelectionData = (payload: any, isCreate: boolean) => {
    const rawList = (Array.isArray(payload?.PrestacionCP) ? payload?.PrestacionCP : payload?.prestacionCP) || [];

    const options: PrestacionFormOption[] = rawList
        .map((item: any) => {
            const prestacionId = getFirstValue(item?.IdPortalPrestacion, item?.idPortalPrestacion, item?.IdPrestacion, item?.idPrestacion, '');
            const expedienteId = getFirstValue(item?.IdExternalPrestacion, item?.idExternalPrestacion, item?.ExpedienteId, item?.expedienteId, null);
            const codigoExpediente = getFirstValue(item?.CodigoExpediente, item?.codigoExpediente, '');

            return {
                prestacionId: toComparableString(prestacionId),
                expedienteId: expedienteId ?? null,
                codigoExpediente: toComparableString(codigoExpediente),
                titulo: `${toComparableString(codigoExpediente)} - ${toComparableString(expedienteId)}`,
            };
        })
        .filter((item: PrestacionFormOption) => item.prestacionId !== '');

    const selectedPrestacionId = isCreate
        ? ''
        : toComparableString(getFirstValue(payload?.PrestacionId, payload?.prestacionId, ''));

    const selectedExpedienteId = isCreate
        ? null
        : getFirstValue(payload?.ExpedienteId, payload?.expedienteId, null);

    const selectedFromList = options.find(
        item => toComparableString(item?.prestacionId) === toComparableString(selectedPrestacionId)
    );

    const codigoExpedienteActual = toComparableString(getFirstValue(payload?.CodigoExpediente, payload?.codigoExpediente, ''));
    const fallbackFromPayload = [codigoExpedienteActual, toComparableString(selectedExpedienteId)]
        .filter(Boolean)
        .join(' - ');

    return {
        options,
        selectedPrestacionId,
        selectedExpedienteId,
        selectedLabel: selectedFromList?.titulo || fallbackFromPayload || '',
    };
};

const FormPrestaciones: React.FC<FormPrestacionesProps> = ({
    info,
    baseName = 'DatosAccidente',
    origen = null,
    expedienteId = null,
    label = 'Prestación relacionada',
    required = 'Debe seleccionar una prestación',
    xl = 4,
    md = 4,
    col = 12,
    onErrorChange,
}) => {
    const { watch, setValue, register } = useFormContext();
    const location = useLocation();

    const origenValue = origen ?? location?.state?.origen ?? null;
    const expedienteIdValue = expedienteId ?? location?.state?.expedienteId ?? null;

    const namePrestacionId = `${baseName}.PrestacionSeleccionada`;
    const nameExpedienteId = `${baseName}.ExpedienteSeleccionado`;
    const namePrestaciones = `${baseName}.PrestacionesCP`;
    const namePrestacionLabel = `${baseName}.PrestacionSeleccionadaLabel`;

    const optionsInForm = (watch(namePrestaciones) || []) as PrestacionFormOption[];
    const selectedPrestacionValue = watch(namePrestacionId);
    const selectedLabelInForm = watch(namePrestacionLabel);

    const lockExpedienteByOrigenActivo = info === true
        && Number(origenValue) === 1
        && expedienteIdValue !== null
        && expedienteIdValue !== undefined
        && expedienteIdValue !== '';

    const expedienteBloqueado = useMemo(() => {
        if (!lockExpedienteByOrigenActivo) return null;

        return optionsInForm.find(
            item => toComparableString(item?.expedienteId) === toComparableString(expedienteIdValue)
        ) || null;
    }, [lockExpedienteByOrigenActivo, optionsInForm, expedienteIdValue]);

    const expedienteNoEncontradoError = lockExpedienteByOrigenActivo && !expedienteBloqueado
        ? `No se encuentra el ExpedienteId ${String(expedienteIdValue)} entre los Expedientes del Usuario.`
        : null;

    const selectedPrestacion = useMemo(() => {
        if (!selectedPrestacionValue) return null;

        return optionsInForm.find(
            item => toComparableString(item?.prestacionId) === toComparableString(selectedPrestacionValue)
        ) || null;
    }, [optionsInForm, selectedPrestacionValue]);

    const dropDownOptions = useMemo(() => {
        return optionsInForm
            .map(item => ({ titulo: item?.titulo, value: item?.prestacionId }))
            .filter(item => toComparableString(item?.value) !== '');
    }, [optionsInForm]);

    const readOnlyLabel = expedienteBloqueado?.titulo
        || selectedPrestacion?.titulo
        || selectedLabelInForm
        || toComparableString(selectedPrestacionValue);

    useEffect(() => {
        if (typeof onErrorChange === 'function') {
            onErrorChange(expedienteNoEncontradoError);
        }
    }, [expedienteNoEncontradoError, onErrorChange]);

    useEffect(() => {
        if (lockExpedienteByOrigenActivo) {
            if (expedienteBloqueado) {
                setValue(namePrestacionId, toComparableString(expedienteBloqueado?.prestacionId));
                setValue(nameExpedienteId, expedienteBloqueado?.expedienteId ?? null);
                setValue(namePrestacionLabel, expedienteBloqueado?.titulo || '');
            } else {
                setValue(namePrestacionId, null);
                setValue(nameExpedienteId, null);
                setValue(namePrestacionLabel, '');
            }
            return;
        }

        if (selectedPrestacion) {
            setValue(nameExpedienteId, selectedPrestacion?.expedienteId ?? null);
            setValue(namePrestacionLabel, selectedPrestacion?.titulo || '');
        } else if (info === true) {
            setValue(nameExpedienteId, null);
            setValue(namePrestacionLabel, '');
        }
    }, [
        lockExpedienteByOrigenActivo,
        expedienteBloqueado,
        selectedPrestacion,
        namePrestacionId,
        nameExpedienteId,
        namePrestacionLabel,
        setValue,
        info,
    ]);

    return (
        <>
            <input type='hidden' {...register(nameExpedienteId)} />
            <input type='hidden' {...register(namePrestacionLabel)} />
            {!info && <input type='hidden' {...register(namePrestacionId)} />}

            {info ? (
                lockExpedienteByOrigenActivo ? (
                    expedienteBloqueado ? (
                        <>
                            <input
                                type='hidden'
                                {...register(namePrestacionId, { required: required || false })}
                            />
                            <FormInputText
                                key={readOnlyLabel}
                                name={`${namePrestacionId}Bloqueado`}
                                id={`${namePrestacionId}Bloqueado`}
                                label={label}
                                md={md}
                                xl={xl}
                                disabled={true}
                                defaultValue={readOnlyLabel}
                            />
                        </>
                    ) : (
                        <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
                            <div className='input-block error'>
                                <label className='input-block__label m-0'>{label}</label>
                                <p className='error m-0'>{expedienteNoEncontradoError}</p>
                            </div>
                        </div>
                    )
                ) : (
                    <FormInputDropDown
                        name={namePrestacionId}
                        label={label}
                        required={required}
                        options={dropDownOptions}
                        xl={xl}
                        md={md}
                        col={col}
                        placeholder='Selecciona un expediente'
                    />
                )
            ) : (
                <FormInputText
                    key={readOnlyLabel}
                    name={`${namePrestacionId}ReadOnly`}
                    id={`${namePrestacionId}ReadOnly`}
                    label={label}
                    md={md}
                    xl={xl}
                    disabled={true}
                    defaultValue={readOnlyLabel}
                />
            )}
        </>
    );
};

export default FormPrestaciones;
